import type { DataGridFilterConfig, FilterConfig } from '@ibsys/kit';
import type { TimeIntervalFormat } from '@/shared/lib/utils/string';
import { ATTRIBUTE_TYPES, DISPLAY_FORMATS } from '../constants';
import { type AttributeDisplayInfo, getAttributeTimeFormat } from './getAttributeDisplayName';
import type { ConversationAttributeItem } from '../api/types/api';

/**
 * Поле ввода границ у отбора-интервала. Границы уезжают числом секунд, поэтому от записи
 * ввода отбор не зависит, а сама запись — `чч:мм:сс`: у `мм:сс` кит ограничивает минуты 59,
 * то есть границу от часа в нём не задать, а уже заданную он теряет — сохранённые 5445 с
 * показал бы как `30:45` вместо `1:30:45`.
 */
const ATTRIBUTE_TIME_RANGE_FILTER = {
  type: 'time-range',
  returnType: 'seconds',
  timeFormat: 'HH:MM:SS',
} as const;

/** Конфиг поля времени, которым вводятся границы отбора по атрибуту-интервалу */
export type AttributeTimeRangeFilter = typeof ATTRIBUTE_TIME_RANGE_FILTER;

/**
 * Поле времени для границ отбора, если значения атрибута показаны как время; `null` — границы
 * вводятся числом (атрибут показан числом секунд либо интервалом не является).
 *
 * Правило выбора одно на все таблицы: построители колонок лишь укладывают его результат в свой
 * тип конфига (`FilterConfig` у `Table`, `DataGridFilterConfig` у `DataGrid`).
 */
export const getAttributeTimeRangeFilter = (
  attribute: AttributeDisplayInfo | undefined,
  timeDisplayFormat: TimeIntervalFormat,
): AttributeTimeRangeFilter | null =>
  getAttributeTimeFormat(attribute, timeDisplayFormat) === DISPLAY_FORMATS.TIME ? ATTRIBUTE_TIME_RANGE_FILTER : null;

/**
 * Вид условия по значению атрибута для `DataGrid`: выбирается по типу атрибута, а у интервала
 * времени — ещё и по действующему формату его показа.
 *
 * Правило одно на все места, где по атрибуту набирают условие: колонки грида диалогов и
 * панели атрибутных фильтров («Стратег», дашборд качества) — расходиться им нельзя.
 * Признак «не пусто» (`enableNotEmptyFilter`) сюда не входит: его понимает не всякий
 * эндпоинт, поэтому добавляет его тот потребитель, чей бэкенд его принимает.
 */
export const getAttributeValueFilter = (
  attribute: ConversationAttributeItem,
  timeDisplayFormat: TimeIntervalFormat,
): DataGridFilterConfig => {
  if (attribute.dataType === ATTRIBUTE_TYPES.SELECT || attribute.dataType === ATTRIBUTE_TYPES.MULTISELECT) {
    return {
      // Бэкенд принимает и скаляр, и массив (см. SearchConversationFilters.attributes).
      type: 'multiselect',
      options: attribute.selectValues?.map((o) => ({ value: o.code, label: o.value })) ?? [],
    };
  }
  if (
    attribute.dataType === ATTRIBUTE_TYPES.TIME ||
    attribute.dataType === ATTRIBUTE_TYPES.INTEGER ||
    attribute.dataType === ATTRIBUTE_TYPES.DECIMAL
  ) {
    // Вид отбора — тем же правилом, что и в классической таблице: расходиться им нельзя
    return getAttributeTimeRangeFilter(attribute, timeDisplayFormat) ?? { type: 'integer' };
  }
  if (attribute.dataType === ATTRIBUTE_TYPES.DATE) return { type: 'date-range' };
  if (attribute.dataType === ATTRIBUTE_TYPES.TIMESTAMP) return { type: 'timestamp-range' };
  if (attribute.dataType === ATTRIBUTE_TYPES.CHECKBOX) return { type: 'boolean' };
  return { type: 'text' };
};

/**
 * Конфиг отбора по колонке таблицы: вид ввода выбирается по типу атрибута, а у интервала
 * времени — ещё и по действующему формату его показа.
 */
export const getAttributeColumnFilters = (
  attribute: ConversationAttributeItem,
  timeDisplayFormat: TimeIntervalFormat,
): FilterConfig => {
  if (attribute.dataType === ATTRIBUTE_TYPES.SELECT || attribute.dataType === ATTRIBUTE_TYPES.MULTISELECT) {
    return {
      type: attribute.dataType === ATTRIBUTE_TYPES.MULTISELECT ? ('multiselect' as const) : ('select' as const),
      path: `attributes.${attribute.path}`,
      options:
        attribute.selectValues?.map((option) => ({
          value: option.code,
          label: option.value,
        })) ?? [],
    };
  }

  if (
    attribute.dataType === ATTRIBUTE_TYPES.TIME ||
    attribute.dataType === ATTRIBUTE_TYPES.INTEGER ||
    attribute.dataType === ATTRIBUTE_TYPES.DECIMAL
  ) {
    // Границы отбора вводятся в том же виде, в каком показано значение: интервал времени —
    // полем времени, число секунд — обычным числом (записи числом секунд у `time-range` нет).
    const timeRange = getAttributeTimeRangeFilter(attribute, timeDisplayFormat);
    if (timeRange) {
      return {
        path: `attributes.${attribute.path}`,
        ...timeRange,
      };
    }

    return {
      path: `attributes.${attribute.path}`,
      type: 'integer' as const,
    };
  }

  if (attribute.dataType === ATTRIBUTE_TYPES.DATE) {
    return {
      path: `attributes.${attribute.path}`,
      type: 'date-range' as const,
    };
  }

  if (attribute.dataType === ATTRIBUTE_TYPES.TIMESTAMP) {
    return {
      path: `attributes.${attribute.path}`,
      type: 'timestamp-range' as const,
    };
  }

  if (attribute.dataType === ATTRIBUTE_TYPES.CHECKBOX) {
    return {
      path: `attributes.${attribute.path}`,
      type: 'boolean' as const,
    };
  }

  return {
    path: `attributes.${attribute.path}`,
    type: 'text' as const,
  };
};
